import { renewalTabs, type RenewalTab } from './renewalExpiryWindow';

/**
 * The Upcoming / Lapsed tab strip, shared by the Renewals list page and the
 * dashboard's Renewals widget (which every role that reaches it -- broker
 * staff and customers -- sees).
 *
 * Cust-4/U-03 (22 Sep): the selected tab used to differ from the idle one by
 * a single shade (red-100 vs red-50), so nobody could tell which was open,
 * and each tab carried a count badge the owner asked to have removed. The
 * selected tab is now a solid fill with white text (the same in dark mode,
 * where a solid fill reads just as clearly), and the idle one is neutral
 * grey, so the selection never depends on telling two tints apart. No counts
 * at all -- the list below each tab says how many rows it holds.
 */
const TAB_TONE: Record<RenewalTab, string> = {
  lapsed: 'border-red-600 bg-red-600 text-white shadow-sm dark:border-red-500 dark:bg-red-600',
  upcoming: 'border-amber-700 bg-amber-700 text-white shadow-sm dark:border-amber-500 dark:bg-amber-700',
};

const IDLE_TONE =
  'border-gray-300 bg-white text-gray-600 hover:bg-gray-50 hover:text-gray-900 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-700 dark:hover:text-white';

const SIZE = {
  sm: 'px-3 py-1.5',
  md: 'px-4 py-2',
} as const;

interface RenewalTabListProps {
  activeTab: RenewalTab;
  onChange: (tab: RenewalTab) => void;
  size?: keyof typeof SIZE;
  className?: string;
}

export function RenewalTabList({ activeTab, onChange, size = 'md', className = '' }: RenewalTabListProps) {
  return (
    <div className={`flex flex-wrap gap-2 ${className}`} role="tablist" aria-label="Renewals">
      {renewalTabs.map((tab) => {
        const selected = activeTab === tab.id;
        return (
          <button
            key={tab.id}
            type="button"
            role="tab"
            aria-selected={selected}
            data-state={selected ? 'active' : 'inactive'}
            onClick={() => onChange(tab.id)}
            className={`rounded-lg border ${SIZE[size]} text-sm transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2 dark:focus-visible:ring-offset-gray-900 ${
              selected ? `font-semibold ${TAB_TONE[tab.id]}` : `font-medium ${IDLE_TONE}`
            }`}
          >
            {tab.label}
          </button>
        );
      })}
    </div>
  );
}
